import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {
  CELL_KEY_SEP,
  CELL_WILDCARD,
  DEFAULT_QUANTITY_BREAK_TIERS,
  ENUM_PROPERTY_VALUES,
  FactorKind,
  isCharacteristicAllowedForKind,
  ItemEnumProperty,
  PROPERTY_CHARACTERISTICS,
  PropertyCharacteristic,
  PropertyCharacteristicGroup,
  quantityBreakKey,
  RateCell,
  RateConfig
} from "../../../../model/rate/RateConfig";
import {NUMBER_UP_VALUES} from "../../../../model/rate/numberUp";
import {LanguagePipe} from "../../../pipe/language.pipe";
import {ConfirmDialogComponent} from "../../confirm-dialog/confirm-dialog.component";
import {PaperTypeService} from "../../../service-data/paper-type.service";
import {PackageTypeService} from "../../../service-data/package-type.service";
import {FacilityService} from "../../../service-data/facility.service";
import {CARRIER_SERVICE_LEVELS} from "../../../util/carrier-service-levels";
import {ValidationResult} from "../../../../model/rate/RateConfigValidator";
import {EMPTY_RESULT, subtreeIssues} from "../validation-paths";

/**
 * One {@link PropertyCharacteristicGroup} editor.
 *
 * The user picks any combination of {@link PropertyCharacteristic}s
 * (chip set). Once picked, the cell-key axis values are enumerated:
 *
 *  - bindingType / interiorType / laminateType: from
 *    {@link ENUM_PROPERTY_VALUES}.
 *  - paperType: live from {@link PaperTypeService} (PaperType.ID
 *    stringified).
 *  - quantityBreak: derived from the scope's `defaultQuantityBreaks`.
 *  - numberUp: from {@link NUMBER_UP_VALUES}.
 *
 * Whenever the characteristic set changes the editor REGENERATES the
 * cell map: existing cells whose key still matches the new tuple
 * shape are preserved (best-effort) and missing tuples get a fresh
 * `{amount: 0}`. A `*` is added per axis as an extra value the user
 * can select (renders as "Any").
 *
 * The actual matrix rendering is delegated to {@link GroupMatrixComponent}.
 */
@Component({
  selector: 'app-rate-group-builder',
  templateUrl: './group-builder.component.html',
  styleUrls: ['./group-builder.component.css']
})
export class GroupBuilderComponent implements OnInit, OnChanges {
  @Input() group: PropertyCharacteristicGroup;
  @Input() scopeConfig: Partial<RateConfig> = {};
  @Input() readOnly: boolean = false;
  /**
   * Parent factor's {@link FactorKind}. Used to filter the
   * characteristic chip list down to the legal subset for the kind
   * (e.g. PER_PACKAGE hides `numberUp`; book-level kinds hide
   * `packageType` and `carrierServiceLevel`). Mirrors the validator's
   * authoritative {@link isCharacteristicAllowedForKind} check so the
   * editor never offers something the validator will reject.
   */
  @Input() factorKind: FactorKind = 'PER_ITEM';
  /** Validator path prefix for THIS group (e.g. `/SHORT_RUN/factors/2/groups/0`). */
  @Input() groupPath: string = '';
  @Input() validation: ValidationResult = EMPTY_RESULT;

  @Output() groupChange = new EventEmitter<PropertyCharacteristicGroup>();
  @Output() groupDelete = new EventEmitter<void>();

  /** All known propertyCharacteristics, in stable order. */
  readonly allCharacteristics: PropertyCharacteristic[] = PROPERTY_CHARACTERISTICS;

  /** Subset of {@link allCharacteristics} legal for the parent factor's kind. */
  get availableCharacteristics(): PropertyCharacteristic[] {
    return PROPERTY_CHARACTERISTICS.filter(c => isCharacteristicAllowedForKind(this.factorKind, c));
  }

  /** Cached value-axes for the current characteristics; keyed positionally. */
  axes: { characteristic: PropertyCharacteristic; values: string[] }[] = [];

  /**
   * UI-only: whether the characteristic chip set + matrix layout bins
   * are visible. Once the user has picked at least one characteristic
   * those controls become rarely-needed clutter, so we collapse them by
   * default; while the group is still empty we leave them open so the
   * user finds them. Per-component-instance state; not persisted to
   * the document. The matrix table itself stays visible regardless.
   */
  builderControlsExpanded: boolean = true;
  /** True until the user (or the auto-default below) toggles. */
  private builderToggledByUser: boolean = false;
  /**
   * True once {@link applyDefaultExpansion} has chosen an initial
   * value. Subsequent `ngOnChanges` runs must NOT auto-collapse the
   * panel — otherwise the moment the user picks their first
   * characteristic chip, the chip set vanishes and they can't pick a
   * second one without first re-opening the panel. The validator
   * force-expand branch still runs every time so a freshly-introduced
   * error always reveals the offending controls.
   */
  private builderInitialExpansionApplied: boolean = false;

  constructor(private dialog: MatDialog,
              public paperTypeService: PaperTypeService,
              public packageTypeService: PackageTypeService,
              public facilityService: FacilityService) {}

  ngOnInit(): void {
    // Kick paperType / packageType / facility load on demand so the chip
    // lists show up. Per AGENTS.md "On-Demand Data Loading", components
    // call getItems() when they need it. packageType is only used by
    // packing rate configs, facility is only meaningful for rate configs
    // that differentiate per-facility (network-level packing rates,
    // SP-level production rates), but loading all three here is cheap
    // and avoids a flicker when the user switches between editor hosts.
    if (!this.paperTypeService.objects || this.paperTypeService.objects.length === 0) {
      this.paperTypeService.getItems().subscribe();
    }
    if (!this.packageTypeService.objects || this.packageTypeService.objects.length === 0) {
      this.packageTypeService.getItems().subscribe();
    }
    if (!this.facilityService.objects || this.facilityService.objects.length === 0) {
      this.facilityService.getItems().subscribe();
    }
    this.refreshAxes();
    this.applyDefaultExpansion();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['group'] || changes['scopeConfig']) { this.refreshAxes(); }
    if (changes['group'] || changes['validation'] || changes['groupPath']) { this.applyDefaultExpansion(); }
    if (changes['group']) { this.healEmptyCells(); }
  }

  /**
   * Auto-heal any cell stored as `{}` (no `amount`, no `infeasible`)
   * by converting it to `{amount: 0}`. Such cells are invalid per the
   * validator's "non-infeasible cell must have a numeric amount" rule
   * but the matrix input shows `0` as a display fallback, leaving the
   * user with no obvious way to resolve the error (typing `0` over an
   * already-displayed `0` doesn't fire a change event, so the saved
   * state stays `{}`).
   *
   * Healing here is a one-time correction on load: if any empty cell
   * is found, emit a fixed group. This marks the editor dirty and the
   * user must click Save to persist — which is the same flow they
   * were already in (the doc has a validation error). If no empty
   * cells are present, this is a no-op.
   *
   * Historically `regenerateCells` seeded the all-`*` fallback as
   * `{}`; that has since been changed to `{amount: 0}`, but pre-
   * existing saved documents may still carry the legacy shape.
   */
  private healEmptyCells(): void {
    if (this.readOnly) { return; }
    const cells = this.group?.cells;
    if (!cells) { return; }
    const keys = Object.keys(cells);
    let mutated = false;
    const next: { [k: string]: RateCell } = {};
    for (const k of keys) {
      const c = cells[k] || {};
      const isEmpty = !c.infeasible && (typeof c.amount !== 'number' || !isFinite(c.amount));
      if (isEmpty) {
        next[k] = {...c, amount: 0};
        mutated = true;
      } else {
        next[k] = c;
      }
    }
    if (mutated) {
      console.log('GroupBuilder: healed empty cell(s) in', this.groupPath, '— converted {} to {amount: 0}');
      // Defer the emit one microtask so we don't mutate during the
      // parent's CD pass (which would trigger an ExpressionChanged
      // error in dev mode).
      Promise.resolve().then(() => this.emit({...this.group, cells: next}));
    }
  }

  /**
   * Show the chip set + layout bins by default while the group is
   * empty (so the user can find the chips); collapse them once any
   * characteristic is picked — but ONLY on the very first pass.
   * Subsequent ngOnChanges runs (triggered every time the user picks
   * another chip) must leave the panel alone, otherwise the panel
   * snaps shut on each click and multi-select becomes impossible.
   *
   * Once the user has clicked the toggle themselves, respect that
   * choice and stop auto-defaulting entirely.
   *
   * If the validator reports errors anywhere in this group's subtree
   * we always force the controls open so the highlighted invalid
   * pieces are visible — regardless of the user's prior toggle. (If
   * the user later collapses while still invalid, that's allowed.)
   */
  private applyDefaultExpansion(): void {
    const hasErrors = this.groupErrorCount > 0;
    if (hasErrors && !this.builderToggledByUser) {
      this.builderControlsExpanded = true;
      return;
    }
    if (this.builderToggledByUser) { return; }
    if (this.builderInitialExpansionApplied) { return; }
    this.builderControlsExpanded = (this.group?.characteristics || []).length === 0;
    this.builderInitialExpansionApplied = true;
  }

  /** Total error count under this group (header badge + force-expand). */
  get groupErrorCount(): number {
    return subtreeIssues(this.validation, this.groupPath).errors.length;
  }

  toggleBuilderControls(): void {
    this.builderControlsExpanded = !this.builderControlsExpanded;
    this.builderToggledByUser = true;
  }

  // ── Axis resolution ──────────────────────────────────────────────

  /** Recompute the value-axes from the current group characteristics. */
  private refreshAxes(): void {
    const out: { characteristic: PropertyCharacteristic; values: string[] }[] = [];
    for (const c of (this.group?.characteristics || [])) {
      out.push({characteristic: c, values: this.valuesFor(c)});
    }
    this.axes = out;
  }

  valuesFor(c: PropertyCharacteristic): string[] {
    switch (c) {
      case 'interiorType':
      case 'bindingType':
      case 'laminateType':
        return [...ENUM_PROPERTY_VALUES[c as ItemEnumProperty]];
      case 'paperType':
        return (this.paperTypeService.objects || []).map(p => '' + p.ID);
      case 'packageType':
        return (this.packageTypeService.objects || []).map(p => '' + p.ID);
      case 'carrierServiceLevel':
        // Sourced from the static {@link CARRIER_SERVICE_LEVELS} list
        // (the single source of truth shared with EcommerceConnection,
        // NetworkSettings, and ChangePackages). The validator skips
        // the precheck for this characteristic so any string is still
        // accepted in cell keys, but the chip set now offers concrete
        // values so authoring is a click rather than a typo-prone
        // free-form text input.
        return CARRIER_SERVICE_LEVELS.map(csl => csl.ID);
      case 'facility':
        // Sourced from {@link FacilityService}'s live data — same
        // dynamic-enum pattern as paperType / packageType. Inactive
        // facilities are filtered out so authoring against a retired
        // PrintNode is impossible from this UI (the validator will
        // still accept any string as a facility token at runtime,
        // matching the `paperType` / `packageType` conventions).
        return (this.facilityService.objects || [])
          .filter(f => f && f.active !== false)
          .map(f => '' + f.ID);
      case 'numberUp':
        return [...NUMBER_UP_VALUES];
      case 'quantityBreak': {
        // Fall back to the global default tiers when the scope hasn't
        // declared its own. The scope-panel UI hint says "No tiers —
        // using defaults", and the engine itself uses the same
        // DEFAULT_QUANTITY_BREAK_TIERS at evaluation time, so the
        // editor must show the same set or the user gets an empty
        // axis (and the matrix renders only the `*` wildcard row/col,
        // making `quantityBreak` look broken).
        const tiers = (this.scopeConfig && this.scopeConfig.defaultQuantityBreaks && this.scopeConfig.defaultQuantityBreaks.length > 0)
          ? this.scopeConfig.defaultQuantityBreaks
          : DEFAULT_QUANTITY_BREAK_TIERS;
        return tiers.map(quantityBreakKey);
      }
    }
  }

  // ── Characteristics chip set ─────────────────────────────────────

  isCharacteristicSelected(c: PropertyCharacteristic): boolean {
    return (this.group?.characteristics || []).indexOf(c) >= 0;
  }

  toggleCharacteristic(c: PropertyCharacteristic): void {
    if (this.readOnly) { return; }
    const current = (this.group.characteristics || []).slice();
    const idx = current.indexOf(c);
    if (idx >= 0) {
      current.splice(idx, 1);
    } else {
      current.push(c);
    }
    // Regenerate cells over the new axes.
    const newAxes = current.map(cc => ({characteristic: cc, values: this.valuesFor(cc)}));
    const newCells = this.regenerateCells(this.group.cells || {}, this.axes, newAxes);
    // Prune _rowAxes / _colAxes to drop any token no longer present in
    // characteristics. The matrix component will re-default + re-balance
    // from there. We don't auto-add the newly-added characteristic to
    // either bin — letting the matrix's defaulting decide keeps the UX
    // predictable (rows fill first when the layout was uneven).
    const charSet = new Set<PropertyCharacteristic>(current);
    const next: PropertyCharacteristicGroup = {characteristics: current, cells: newCells};
    if (this.group._rowAxes && this.group._rowAxes.length > 0) {
      const pruned = this.group._rowAxes.filter(x => charSet.has(x));
      if (pruned.length > 0) { next._rowAxes = pruned; }
    }
    if (this.group._colAxes && this.group._colAxes.length > 0) {
      const pruned = this.group._colAxes.filter(x => charSet.has(x));
      if (pruned.length > 0) { next._colAxes = pruned; }
    }
    this.emit(next);
  }

  /**
   * Best-effort cell migration when the user toggles characteristics on
   * or off. We DO NOT seed a dense Cartesian product of cells — that
   * produced an overwhelming sea of zero-amount placeholders for groups
   * with even modest axes (e.g. `paperType` × `quantityBreak` →
   * dozens of cells). Instead we:
   *
   *  1. Carry forward every existing cell whose key still has the
   *     right number of tokens for the new axis count, projecting
   *     across reorderings (paperType moved from position 0 to
   *     position 2 still maps).
   *  2. Drop cells whose old characteristic shape doesn't survive the
   *     change (validator coverage check will catch any resulting
   *     gaps).
   *  3. If the result is empty, seed exactly ONE all-`*` fallback cell
   *     with `{}` (an empty cell, zero contribution, fall-through).
   *     This keeps `cells` non-empty (validator requires that) while
   *     forcing the user to author at least one valued cell to make
   *     the group meaningful.
   *
   * Cells are never auto-populated with `{amount: 0}` — that
   * conflated "user wanted 0" with "placeholder". The new "empty
   * cell" semantic (see RateConfig.ts CELL doc) lets us leave cells
   * truly unset.
   */
  private regenerateCells(oldCells: { [k: string]: RateCell },
                          oldAxes: { characteristic: PropertyCharacteristic; values: string[] }[],
                          newAxes: { characteristic: PropertyCharacteristic; values: string[] }[]): { [k: string]: RateCell } {
    const newCells: { [k: string]: RateCell } = {};
    const newAxisCount = newAxes.length;
    const oldAxisCount = oldAxes.length;
    if (newAxisCount === 0) {
      // empty-axis group is meaningless; the parent should drop the group.
      return {};
    }

    // Carry forward cells whose token count still matches the new axis
    // count. When axes have been reordered, project the old key into
    // the new positional order; characteristics that exist in only one
    // of the two axis sets get a `*` token in the projected key.
    if (oldAxisCount === newAxisCount && oldAxisCount > 0) {
      const oldCharIndex: { [c: string]: number } = {};
      oldAxes.forEach((a, i) => oldCharIndex[a.characteristic] = i);
      for (const oldKey of Object.keys(oldCells)) {
        const oldTokens = oldKey.split(CELL_KEY_SEP);
        if (oldTokens.length !== oldAxisCount) { continue; }
        const newTokens: string[] = new Array(newAxisCount);
        for (let i = 0; i < newAxisCount; i++) {
          const newChar = newAxes[i].characteristic;
          const oldPos = oldCharIndex[newChar];
          newTokens[i] = oldPos == null ? CELL_WILDCARD : oldTokens[oldPos];
        }
        newCells[newTokens.join(CELL_KEY_SEP)] = {...oldCells[oldKey]};
      }
    }

    // If we ended up with no cells at all, seed exactly one all-`*`
    // fallback cell with `{amount: 0}` so the validator's
    // "cells must be non-empty" AND "non-infeasible cell must have
    // a numeric amount" rules are both satisfied out of the box. The
    // user can then edit the displayed `0` to author a real value
    // (or toggle the cell to `infeasible`). Seeding `{}` (empty cell)
    // instead would dead-end the user with a validation error and no
    // matrix-cell affordance to clear it — the matrix renders an
    // input that shows `0` as a display fallback, but the cell
    // remains unset until a `change` event fires, which never
    // happens if the user doesn't realise the cell needs editing.
    if (Object.keys(newCells).length === 0) {
      newCells[new Array(newAxisCount).fill(CELL_WILDCARD).join(CELL_KEY_SEP)] = {amount: 0};
    }
    return newCells;
  }

  // ── Cell mutations (forwarded from the matrix component) ─────────

  /**
   * Set a cell's amount. A non-finite value (`NaN` from clearing the
   * input, or `Infinity`) DELETES the cell entirely so resolution
   * falls through to a wider-wildcard cell. Authoring a literal `0`
   * keeps the cell with `{amount: 0}` — that's a different intent
   * from "no value here".
   */
  setCellAmount(comboKey: string, amount: number): void {
    if (this.readOnly) { return; }
    const cells = {...(this.group.cells || {})};
    if (!isFinite(amount)) {
      delete cells[comboKey];
      this.emit({...this.group, cells});
      return;
    }
    const cur = cells[comboKey] || {};
    cells[comboKey] = {...cur, amount};
    if (cells[comboKey].infeasible) { delete cells[comboKey].infeasible; }
    this.emit({...this.group, cells});
  }

  /**
   * Toggle a cell between "infeasible" and "not infeasible". Toggling
   * OFF removes the cell entirely (rather than seeding `{amount: 0}`)
   * so resolution falls through to a wider-wildcard cell. The user
   * authors a real amount to make the cell contribute.
   */
  toggleCellInfeasible(comboKey: string): void {
    if (this.readOnly) { return; }
    const cells = {...(this.group.cells || {})};
    const cur = cells[comboKey] || {};
    if (cur.infeasible) {
      delete cells[comboKey];
    } else {
      cells[comboKey] = {infeasible: true};
    }
    this.emit({...this.group, cells});
  }

  /**
   * Persist a new pivot layout chosen by the user (drag-drop chip bins
   * or "swap axes" button on the matrix). The arrays are EDITOR-ONLY
   * companion fields — see the `_rowAxes` / `_colAxes` JSDoc on
   * `PropertyCharacteristicGroup`. They are written inline on the
   * group and round-tripped through the server unchanged.
   *
   * Empty arrays are stored as `undefined` so JSON output stays
   * minimal when the user has not made an explicit choice.
   */
  setAxesLayout(rowAxes: PropertyCharacteristic[], colAxes: PropertyCharacteristic[]): void {
    if (this.readOnly) { return; }
    const next: PropertyCharacteristicGroup = {...this.group};
    if (rowAxes && rowAxes.length > 0) { next._rowAxes = rowAxes.slice(); } else { delete next._rowAxes; }
    if (colAxes && colAxes.length > 0) { next._colAxes = colAxes.slice(); } else { delete next._colAxes; }
    this.emit(next);
  }


  // ── Group delete ─────────────────────────────────────────────────

  confirmDeleteGroup(): void {
    if (this.readOnly) { return; }
    const ref = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: {
        title: LanguagePipe.get('Delete group', 'compressSpaces') + LanguagePipe.get('?', 'compressSpaces'),
        body: LanguagePipe.get('This cannot be undone.', 'compressSpaces'),
        noButton: LanguagePipe.get('Cancel', 'compressSpaces'),
        yesButton: LanguagePipe.get('Delete', 'compressSpaces'),
        returnValue: 'ok'
      }
    });
    ref.afterClosed().subscribe(result => {
      if (result === 'ok') { this.groupDelete.emit(); }
    });
  }

  /** Friendly label for a propertyCharacteristic chip. */
  characteristicLabel(c: PropertyCharacteristic): string {
    switch (c) {
      case 'bindingType':         return LanguagePipe.get('Binding type', 'compressSpaces');
      case 'interiorType':        return LanguagePipe.get('Interior type', 'compressSpaces');
      case 'laminateType':        return LanguagePipe.get('Laminate type', 'compressSpaces');
      case 'paperType':           return LanguagePipe.get('Paper type', 'compressSpaces');
      case 'packageType':         return LanguagePipe.get('Package type', 'compressSpaces');
      case 'carrierServiceLevel': return LanguagePipe.get('Carrier service level', 'compressSpaces');
      case 'facility':            return LanguagePipe.get('Facility', 'compressSpaces');
      case 'quantityBreak':       return LanguagePipe.get('Quantity break', 'compressSpaces');
      case 'numberUp':            return LanguagePipe.get('Number up', 'compressSpaces');
      default: return c;
    }
  }

  private emit(next: PropertyCharacteristicGroup): void {
    this.groupChange.emit(next);
    this.refreshAxes();
  }
}

